import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { IconInfoCircle } from '@tabler/icons-react'
import { isEmpty, isNil } from 'lodash'
import classNames from 'classnames'
import pluralize from 'pluralize'

import Select from '@/components/forms/Select'
import { Pill } from '@/components/pills/Pill'
import { Drag, Drop, DragAndDrop } from '@/components/dragDrop'
import { toast } from '@/components/toasts/toast.helpers'

import { getRowTypeKey, normalizeFieldKey } from '../helpers'
import { FIELD_GROUP } from '../helpers/fieldGroups.helpers'
import {
  buildOptionNameIndex,
  buildPendingOptionLabels,
  describeLink,
  findPromptField,
} from '../helpers/fieldLinks.helpers'
import {
  describeTokenProblems,
  findTokenProblems,
  getTokenSuggestions,
} from '../helpers/fieldTokens.helpers'
import {
  ANY_VALUE,
  buildOptionsByFieldName,
  getBranchChoices,
  getBranchConditionFields,
  planBranchDrop,
  PROVENANCE,
  resolveBranch,
} from '../helpers/branches.helpers'
import { FieldTypePill } from './FieldTypePill'
import { PromptDescriptionField } from './PromptDescriptionField'

// The field list read the other way round. Instead of a field per row with the
// conditions that gate it, you pick what a call answered and get the note that
// call produces — because on a flow where one call-type field gates sixty
// others, "which fields does this call get" is the question the field list
// can't answer, and no single list of all sixty is a note anyone ever sees.
//
// The rows deliberately don't restate the condition that was just chosen. They
// say where the field came from instead: on every call, only this branch, or
// shared with N others — the thing you can't work out from the picker above.
//
// The chosen branch is owned by the editor rather than held here, so switching
// to the field list and back doesn't drop it — the two views are readings of one
// flow, and hopping to the fields to check a description shouldn't cost the
// branch you were reading. The search box is a transient lens and stays local.
//
// @param {object[]} props.promptFields the flow's fields, in note order
// @param {object[]} props.fieldLinks the gating conditions between them
// @param {object} props.selection chosen value per condition field, by normalized name
// @param {Function} props.onSelectionChange called with the next selection
// @param {boolean} [props.groupByAgentVisibility] split the results the way the
//   field list does — only the call channel honours show_to_user
// @param {Function} [props.onUpdateField] (fieldKey, nextField) — absent when read-only
// @param {Function} [props.onReorder] called with the next prompt_fields
// @param {object} [props.pendingTaxonomies] option edits applied for testing but
//   not yet saved, by taxonomy uuid
// @param {object} [props.flags] LaunchDarkly flags, for the Summary lock
// Named and ordered as the field list and the test results are: the agent's
// fields run first at call end on a latency budget, the rest follow.
const VISIBILITY_GROUPS = [
  { id: FIELD_GROUP.SHOWN_TO_AGENT, title: 'Shown to Agent', isShown: true },
  { id: FIELD_GROUP.PROCESSED_LATER, title: 'Processed Later', isShown: false },
]

export const BranchesView = ({
  promptFields = [],
  fieldLinks = [],
  selection = {},
  onSelectionChange,
  groupByAgentVisibility = false,
  onUpdateField,
  onReorder,
  pendingTaxonomies = {},
  flags,
}) => {
  const { rtnFieldNames, rtnBitFlow } = useSelector((state) => state.playbook)
  // Only a reference the flow used to resolve is worth flagging; see
  // findTokenProblems.
  const loadedFieldNames = (rtnBitFlow?.fields ?? []).map((field) => field.field_name)
  const [expandedKey, setExpandedKey] = useState(null)
  const canEdit = !isNil(onUpdateField)
  const canReorder = !isNil(onReorder)

  const optionsByFieldName = buildOptionsByFieldName(rtnFieldNames)
  const optionNames = {
    ...buildOptionNameIndex(rtnFieldNames),
    ...buildPendingOptionLabels(pendingTaxonomies),
  }
  const { rows, skipped } = resolveBranch({ promptFields, links: fieldLinks, selection })

  // Only conditions the current branch actually reaches are worth asking about —
  // a nested one whose parent is closed has nothing left to decide.
  const reachableKeys = new Set(rows.map((row) => normalizeFieldKey(row.field.key)))
  const conditionFields = getBranchConditionFields(promptFields, fieldLinks).filter((condition) =>
    reachableKeys.has(normalizeFieldKey(condition.field.key))
  )
  // Only a choice on a condition still on screen counts. A field deleted from
  // the Fields view can leave a value behind, and acting on one nothing shows
  // would list a branch with no way to see or change what picked it.
  const hasChosen = conditionFields.some(
    (condition) => (selection[normalizeFieldKey(condition.field.key)] ?? ANY_VALUE) !== ANY_VALUE
  )

  const chooseValue = (fieldKey, value) => {
    const nextSelection = { ...selection, [normalizeFieldKey(fieldKey)]: value }
    // A condition that just went out of reach must forget its value, or coming
    // back to this branch would silently reapply a choice nothing is showing.
    const stillReachable = resolveBranch({
      promptFields,
      links: fieldLinks,
      selection: nextSelection,
    })
    const liveKeys = new Set(stillReachable.rows.map((row) => normalizeFieldKey(row.field.key)))
    Object.keys(nextSelection).forEach((key) => {
      if (!liveKeys.has(key)) {
        delete nextSelection[key]
      }
    })
    onSelectionChange(nextSelection)
  }

  const renderProvenance = (row) => {
    // A presence gate names no value, so there is no count of branches to give
    // — the condition itself is the whole answer.
    if (row.provenance === PROVENANCE.presence) {
      return (
        <Pill small outlined className="rtn-branch-prov">
          {describeLink(
            row.decidingLink,
            findPromptField(promptFields, row.decidingLink.source),
            optionNames
          )}
        </Pill>
      )
    }
    if (row.provenance === PROVENANCE.conditional) {
      return (
        <Pill small className="rtn-branch-prov rtn-branch-prov--conditional">
          {describeLink(
            row.decidingLink,
            findPromptField(promptFields, row.decidingLink.source),
            optionNames
          )}
        </Pill>
      )
    }
    if (row.provenance === PROVENANCE.always) {
      return (
        <Pill small brand className="rtn-branch-prov">
          every call
        </Pill>
      )
    }
    const sourceName = row.decidingLink.source
    if (row.provenance === PROVENANCE.only) {
      return (
        <Pill small className="rtn-branch-prov rtn-branch-prov--only">
          only this {sourceName}
        </Pill>
      )
    }
    return (
      <Pill small caution className="rtn-branch-prov">
        shared · {row.reach} {pluralize(sourceName, row.reach)}
      </Pill>
    )
  }

  // The same rule the field list applies: a description that resolves to the org
  // default belongs to the default, and Summary's is gated on its own flag.
  const isDescriptionLocked = (field) =>
    field.inheritsDefault === true ||
    (field.key === 'Summary' && flags?.summaryEditEnabled !== true)

  // The rows of one drop list. Ungrouped, there is a single list holding them all.
  const groupRowsFor = (dropId) => {
    if (!groupByAgentVisibility) {
      return rows
    }
    const group = VISIBILITY_GROUPS.find((candidate) => candidate.id === dropId)
    return rows.filter((row) => (row.field.show_to_user === true) === group.isShown)
  }

  const handleDragEnd = ({ source, destination }) => {
    if (isNil(destination) || !canReorder) {
      return
    }
    const rowsByGroup = groupByAgentVisibility
      ? {
          [FIELD_GROUP.SHOWN_TO_AGENT]: groupRowsFor(FIELD_GROUP.SHOWN_TO_AGENT),
          [FIELD_GROUP.PROCESSED_LATER]: groupRowsFor(FIELD_GROUP.PROCESSED_LATER),
        }
      : { [FIELD_GROUP.SHOWN_TO_AGENT]: rows }

    const plan = planBranchDrop({
      promptFields,
      links: fieldLinks,
      rowsByGroup,
      source,
      destination,
    })
    if (!isEmpty(plan.blockedReason)) {
      toast.error(plan.blockedReason)
      return
    }
    onReorder(plan.promptFields)
  }

  const renderRows = (groupRows, dropId) => (
    <Drop droppableId={dropId} className="rtn-branches__list" data-testid="branch-results">
      {groupRows.map((row, position) => {
        const fieldKey = normalizeFieldKey(row.field.key)
        const isOpen = expandedKey === fieldKey
        const locked = isDescriptionLocked(row.field)
        return (
          <Drag
            draggableId={row.field.key}
            index={position}
            key={row.field.key}
            // The grip sits in its own column rather than a negative-margin
            // gutter: this list has no gutter to pull into, and the row would
            // hang 43px past its border and take the provenance pill with it.
            readOnly={!canReorder}
            alwaysShowIcon
            dataTestId={`branch-drag-${fieldKey}`}
          >
            {/* One child: Drag lays the grip and its content out on a two-column
                grid, so a second element here drops into the grip's 35px
                column instead of below the row. */}
            <div className="rtn-branches__row-group">
              <div
                className={classNames('rtn-branches__row', {
                  'rtn-branches__row--clickable': canEdit,
                  'rtn-branches__row--open': isOpen,
                })}
                data-testid="branch-row"
                {...(canEdit
                  ? {
                      role: 'button',
                      tabIndex: 0,
                      'aria-expanded': isOpen,
                      onClick: () => setExpandedKey(isOpen ? null : fieldKey),
                      onKeyDown: (event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          setExpandedKey(isOpen ? null : fieldKey)
                        }
                      },
                    }
                  : {})}
              >
                <span className="rtn-branches__name">{row.field.key}</span>
                <FieldTypePill typeKey={getRowTypeKey(row.field)} />
                {row.gatesCount > 0 && (
                  <Pill small brand className="rtn-branches__gates">
                    condition field → {row.gatesCount}
                  </Pill>
                )}
                <span className="rtn-branches__desc">{row.field.value}</span>
                {renderProvenance(row)}
              </div>

              {isOpen && (
                <div className="rtn-branches__editor" data-testid="branch-row-editor">
                  {/* The shared description field rather than a bare textarea,
                      so a branch gets the same `$` completion, reference
                      warnings and debounced commit the field list has. No
                      visible header — the row above already names the field. */}
                  <PromptDescriptionField
                    name={`branch-desc-${fieldKey}`}
                    ariaLabel={`${row.field.key} description`}
                    value={row.field.value ?? ''}
                    disabled={locked}
                    onCommit={(next) => onUpdateField(row.field.key, { ...row.field, value: next })}
                    referenceError={describeTokenProblems(
                      findTokenProblems(row.field, promptFields, fieldLinks, loadedFieldNames)
                    )}
                    tokenSuggestionsFor={(query) =>
                      getTokenSuggestions(row.field, promptFields, fieldLinks, query)
                    }
                    testId="branch-row-description"
                  />
                  {locked && (
                    <p className="muted-text">
                      This description comes from your organization defaults. Change it there, or
                      break this field from them in the Fields view.
                    </p>
                  )}
                </div>
              )}
            </div>
          </Drag>
        )
      })}
    </Drop>
  )

  if (isEmpty(conditionFields)) {
    return (
      <div className="rtn-branches" data-testid="branches-view">
        <div className="rtn-branches__empty" data-testid="branches-no-conditions">
          <IconInfoCircle />
          <span>
            No field is gated on another yet, so every call produces the same note. Add a condition
            from the Fields view and this becomes a preview of what each call gets.
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className="rtn-branches" data-testid="branches-view">
      <div className="rtn-branches__picker">
        {conditionFields.map((condition) => {
          const fieldKey = normalizeFieldKey(condition.field.key)
          const choices = getBranchChoices(
            condition.field,
            fieldLinks,
            optionsByFieldName,
            pendingTaxonomies
          )
          return (
            <div className="rtn-branches__picker-row" key={fieldKey}>
              <label className="rtn-branches__picker-label" htmlFor={`branch-${fieldKey}`}>
                {condition.field.key}
              </label>
              {/* Unchosen is its own state — the rows below say what would
                  admit them — so it reads as an empty control rather than an
                  "any" option competing with the real ones. Clearing goes back
                  to it. */}
              <Select
                inputId={`branch-${fieldKey}`}
                options={choices}
                value={selection[fieldKey] ?? ''}
                onChange={(option) => chooseValue(fieldKey, option?.value ?? ANY_VALUE)}
                placeholder={`Choose a ${condition.field.key}`}
              />
              <span className="rtn-branches__picker-note">
                gates {condition.gatesCount} {pluralize('field', condition.gatesCount)}
              </span>
            </div>
          )
        })}
      </div>

      {/* Nothing is listed until a value is chosen: with every gate open the
          list would be every field in the flow, which is a note no call ever
          produces — the one reading this view exists to avoid. */}
      {hasChosen ? (
        <>
          <div className="rtn-branches__results-head">
            <h4>Fields run on this call</h4>
            <span className="rtn-branches__results-meta" data-testid="branch-results-meta">
              <b>{rows.length}</b> of {promptFields.length} fields · in the order they appear in the
              note
            </span>
          </div>

          {/* Split the same way the field list is, because "runs" and "the
              agent sees it" are different questions and this view answers both
              at once — a branch can run twenty fields and put two in front of
              the agent. */}
          <DragAndDrop onDragEnd={handleDragEnd}>
            {groupByAgentVisibility
              ? VISIBILITY_GROUPS.map((group) => {
                  const groupRows = groupRowsFor(group.id)
                  // An empty group is noise to read but a necessary target to
                  // drag into, so it shows only where a drag can happen — and
                  // there it also says this call runs nothing in that group.
                  if (isEmpty(groupRows) && !canReorder) {
                    return null
                  }
                  return (
                    <div
                      className="rtn-branches__group"
                      key={group.id}
                      data-testid={`branch-group-${group.id}`}
                    >
                      <h5 className="rtn-branches__group-title">{group.title}</h5>
                      {renderRows(groupRows, group.id)}
                    </div>
                  )
                })
              : renderRows(rows, FIELD_GROUP.SHOWN_TO_AGENT)}
          </DragAndDrop>

          {!isEmpty(skipped) && (
            <p className="muted-text rtn-branches__skipped" data-testid="branch-skipped">
              {skipped.length} {pluralize('field', skipped.length)}{' '}
              {skipped.length === 1 ? 'is' : 'are'} skipped here. Pick another value to see theirs.
            </p>
          )}
        </>
      ) : (
        <div className="rtn-branches__empty" data-testid="branches-empty">
          <IconInfoCircle />
          <span>Choose an option to see a call&apos;s note based on the selection.</span>
        </div>
      )}
    </div>
  )
}
